import React, { useContext, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import ImageQuestion from '@/assets/img/image-question.svg';
import logo from '@/assets/img/logo.svg';
import ModalDefault from '@/components/Modals/ModalDefault';
import CnpjTextField from '@/components/NumberFormat/cnpjTextField';
import { QuotationAnswerContext } from '@/contexts/QuotationAnswerContext';
import { Toast } from '@/lib/Toast';
import { hasParamsMarketing, validateCnpj } from '@/utils/validates';
import { ArrowBack, InfoOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';

interface IModalCnpjCadastre {
  isOpen: boolean;
  closeModal?: () => void;
  handleSubmitNewCnpj: (value: string) => Promise<void>;
  textButton: 'Próximo' | 'Cadastrar';
}

export default function ModalCnpjCadastre({
  isOpen,
  closeModal,
  handleSubmitNewCnpj,
  textButton,
}: IModalCnpjCadastre) {
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));
  const { quotationAnswer } = useContext(QuotationAnswerContext);
  const router = useRouter();
  const [selectedWithoutCnpj, setSelectedWithoutCnpj] = useState(false);
  const [isOpenModalQuestionCNPJ, setIsOpenModalQuestionCNPJ] = useState(false);
  const [cnpj, setCnpj] = useState<string | undefined>(
    hasParamsMarketing(router) && router.query.cnpj !== 'modal_true'
      ? router.query.cnpj?.toString()
      : quotationAnswer.cnpj
  );
  const handleCloseModalQuestionCNPJ = () => {
    setIsOpenModalQuestionCNPJ(false);
  };
  const handleCnpjChange = (value: string) => {
    setCnpj(value);
  };

  const handleCheckedCnpj = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedWithoutCnpj(!selectedWithoutCnpj);
    setCnpj(event.target.checked ? '' : cnpj);
  };

  const handleSendCnpj = () => {
    if (selectedWithoutCnpj) {
      setCnpj('');
      handleStepCadastre();
      return;
    }
    if (!cnpj) {
      Toast('error', 'Informe um CNPJ válido ou selecione "Não possuo CNPJ"');
      return;
    }
    if (
      (hasParamsMarketing(router) && cnpj && !validateCnpj(cnpj)) ||
      (quotationAnswer.cnpj && !validateCnpj(quotationAnswer.cnpj))
    ) {
      Toast('error', 'CNPJ inválido');
      return;
    }
    handleStepCadastre();
  };

  const handleStepCadastre = () => {
    setLoading(true);
    handleSubmitNewCnpj(cnpj || '').finally(() => {
      return setLoading(false);
    });
  };

  return (
    <Modal
      open={isOpen}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <>
        <Box
          sx={{
            display: 'flex',
            backgroundColor: 'primary.main',
            width: '100%',
            height: '74px',
            justifyContent: 'center',
          }}
        >
          <Image height="10px" width="220px" src={logo} alt="Logo" />
        </Box>
        <Box
          sx={{
            backgroundColor: smUp ? 'primary.contrastText' : 'primary.main',
            display: 'grid',
            placeItems: 'center',
            height: 'calc(100% - 74px)',
          }}
        >
          <Box
            sx={{
              borderRadius: `10px ${!smUp ? '10px 0 0' : ''}`,
              backgroundColor: 'primary.contrastText',
              height: smUp ? '70%' : '100%',
              width: smUp ? '456px' : '100vw',
              padding: smUp ? '38px' : '38px 16px',
              margin: '0 auto',
              boxShadow: smUp ? '0px 4px 4px rgba(0, 0, 0, 0.25)' : 'none',
              position: 'relative',
              zIndex: '2',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Box
              component="span"
              sx={{
                position: 'absolute',
                left: '16px',
                top: '16px',
              }}
            >
              {closeModal && (
                <IconButton onClick={closeModal}>
                  <ArrowBack />
                </IconButton>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'start',
                  height: '100%',
                }}
              >
                <Typography
                  sx={{ my: 2, color: '#808080' }}
                  id="modal-modal-title"
                  variant="h6"
                >
                  Qual o CNPJ da sua empresa?
                </Typography>
                <TextField
                  required
                  variant="standard"
                  placeholder="000.000.000-00"
                  name="cnpj"
                  label="CNPJ"
                  id="cnpj"
                  value={cnpj}
                  onChange={event => {
                    handleCnpjChange(event.target.value);
                  }}
                  InputProps={{
                    inputComponent: CnpjTextField as never,
                  }}
                  sx={{ mb: 2, width: '100%' }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={handleCheckedCnpj}
                      checked={selectedWithoutCnpj}
                    />
                  }
                  label={
                    <Typography variant="body2">Não possuo CNPJ</Typography>
                  }
                />
                <Button
                  startIcon={<InfoOutlined />}
                  onClick={() => setIsOpenModalQuestionCNPJ(true)}
                  sx={{
                    textTransform: 'none',
                    mt: 2,
                    textDecoration: 'underline',
                    pl: 0.5,
                  }}
                >
                  Por que preciso informar o meu CNPJ?
                </Button>
              </Box>
              <Box sx={{ mt: 5 }}>
                <LoadingButton
                  loading={loading}
                  fullWidth
                  variant="contained"
                  sx={{ backgroundColor: '#00A8F3', color: 'white' }}
                  onClick={handleSendCnpj}
                >
                  {textButton}
                </LoadingButton>
              </Box>
            </Box>
          </Box>
        </Box>
        <ModalDefault
          isOpen={isOpenModalQuestionCNPJ}
          onClose={() => handleCloseModalQuestionCNPJ()}
          src={ImageQuestion}
          alt="Imagem por que precisa informar CPF"
        >
          {
            <Box sx={{ textAlign: 'left' }}>
              <Typography color="#808080" variant="subtitle1">
                Por que preciso informar o CNPJ da minha empresa?
              </Typography>
              <Typography
                color="#808080"
                variant="body2"
                sx={{ fontWeight: '300' }}
              >
                Para podermos divulgar a sua empresa para mais de 1500
                construtores que utilizam o sistema Obra Prima.
              </Typography>
            </Box>
          }
        </ModalDefault>
      </>
    </Modal>
  );
}
